import type { Block } from 'blockly/core';

import {
  BEGINNER_TIME_UNIT_OPTIONS,
  DEFAULT_BEGINNER_TIME,
  beginnerTimeToMilliseconds,
  normalizeBeginnerTimeValue,
  type BeginnerTimeValue,
} from '../constants.js';
import type { FieldBeginnerTime } from '../fields/field-beginner-time.js';
import {
  beginnerPinField,
  getBeginnerPinOptions,
  type BeginnerPinRole,
} from '../pins/pin-selector.js';

export function getBeginnerLedPinOptions(): [string, string][] {
  return getBeginnerPinOptions('digital');
}

export function getBeginnerRgbPinOptions(): [string, string][] {
  return getBeginnerPinOptions('digital');
}

export function getBeginnerPixelDataPinOptions(): [string, string][] {
  return getBeginnerPinOptions('digital');
}

export function getBeginnerButtonPinOptions(): [string, string][] {
  return getBeginnerPinOptions('button');
}

export { beginnerPinField, getBeginnerPinOptions };
export type { BeginnerPinRole };

export function beginnerTimeField(
  name: string,
  config?: {
    value?: number;
    unit?: BeginnerTimeValue['unit'];
    min?: number;
    max?: number;
    precision?: number;
  },
) {
  return {
    type: 'field_beginner_time',
    name,
    value: config?.value ?? DEFAULT_BEGINNER_TIME.value,
    unit: config?.unit ?? DEFAULT_BEGINNER_TIME.unit,
    min: config?.min ?? 0,
    max: config?.max,
    precision: config?.precision ?? 0.1,
  };
}

/**
 * Unit-only selector (Seconds / Milliseconds) for reporter Time blocks.
 * Reuses the same unit options as field_beginner_time.
 */
export function beginnerTimeUnitField(name = 'UNIT') {
  return {
    type: 'field_dropdown',
    name,
    options: BEGINNER_TIME_UNIT_OPTIONS.map(([label, value]) => [label, value]),
  };
}

export function brightnessPercentField(name: string, value = 100) {
  return {
    type: 'field_number',
    name,
    value,
    min: 0,
    max: 100,
    precision: 1,
  };
}

export function readBeginnerTimeField(block: Block, fieldName: string): BeginnerTimeValue {
  const field = block.getField(fieldName) as FieldBeginnerTime | null;
  if (!field) {
    return DEFAULT_BEGINNER_TIME;
  }
  return normalizeBeginnerTimeValue(field.getValue());
}

export function readBeginnerTimeMilliseconds(block: Block, fieldName: string): number {
  return beginnerTimeToMilliseconds(readBeginnerTimeField(block, fieldName));
}
